"use client";

// Non-blocking DexTools co-branding BAR for widget landings. Replaces the
// earlier tiny inline chip, which was too faint to read as a partner banner
// ("apenas se ve"). This is a full-width bar mounted above the event hero and
// above the trader profile. Self-gated: renders only on a real DexTools
// landing (the populator set `ctx`/`traderCtx`) while logged out, and vanishes
// once the visitor registers. Safe to drop anywhere — returns null off a
// DexTools landing, so it never leaks onto normal pages.

import { useDextoolsSignupStore } from "@/stores/useDextoolsSignupStore";
import { useAuth } from "@/hooks/useAuth";
import { useT } from "@/hooks/useT";

export default function DextoolsLandingBar({ fullWidth = false }: { fullWidth?: boolean }) {
  const t = useT();
  const { isLoggedIn, isReady } = useAuth();
  const ctx = useDextoolsSignupStore((s) => s.ctx);
  const traderCtx = useDextoolsSignupStore((s) => s.traderCtx);

  // In local dev the mock user is always "logged in" — allow a preview there
  // so the bar stays testable.
  const devPreview = process.env.NODE_ENV !== "production";
  if ((!ctx && !traderCtx) || !isReady || (isLoggedIn && !devPreview)) return null;

  return (
    // `fullWidth` → spans the column (event hero). Otherwise inline-flex so the
    // pill hugs its content (trader profile). No horizontal padding on the
    // wrapper so it aligns with the left edge of the block below; mb-3 separates.
    <div className="mb-3">
      <div className={`${fullWidth ? "flex justify-center w-full" : "inline-flex"} items-center gap-2 rounded-[5px] border border-[#1fc2db]/20 bg-[#1fc2db]/[0.06] dark:bg-[#1fc2db]/[0.05] px-3 py-1.5`}>
        <span className="text-[11px] font-semibold uppercase tracking-wider text-[#1597ad] dark:text-[#1fc2db]">
          {t("dextools.landing.via")}
        </span>
        {/* The logo IS the DexTools wordmark — it stands in for the brand name
            so the text doesn't repeat it. */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src="/logos/dext.png" alt="DexTools" className="h-6 w-auto" />
        <span className="text-[11px] font-semibold uppercase tracking-wider text-[#1597ad] dark:text-[#1fc2db]">
          · {t("dextools.landing.officialPartner")}
        </span>
      </div>
    </div>
  );
}
